import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// ── Embedded-app bounce ──────────────────────────────────────────────
// Session tokens (our API auth) only exist inside the Shopify admin
// iframe, so top-level loads of this SPA must be routed back into the
// admin. CRITICAL: they must be routed THROUGH the backend OAuth entry
// (/api/auth/install), never straight to https://<shop>/admin/apps/<key>.
// With use_legacy_install_flow, a FRESH App Store install loads this
// App URL top-level with ?shop=…&hmac=… BEFORE the app is installed —
// jumping straight to /admin/apps/<key> at that point hits Shopify
// admin's "There's no page at this address" 404 (the app isn't
// installed yet, so the route doesn't exist). Going through /install
// is always safe: not installed → OAuth grant screen; already
// installed → Shopify round-trips the authorize redirect instantly
// (no prompt) and the callback lands the merchant in the embedded app.
(function bounceIntoAdmin() {
  if (typeof window === 'undefined') return;
  let embedded = false;
  try { embedded = window.top !== window.self; } catch { embedded = true; }
  if (embedded) return;                                  // already in admin
  if (process.env.NODE_ENV !== 'production') return;     // local dev
  if (window.location.pathname.startsWith('/invite')) return; // public page
  if (window.location.pathname.startsWith('/r/')) return;     // public referral
  // Theme-editor code page opened in a new tab from the panel: it hops
  // into the embedded app itself, keeping its path (the install bounce
  // below would land on the app home and lose it).
  if (window.location.pathname.startsWith('/theme-editor-')) return;

  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (!apiBase) return;

  // Backend deep links (e.g. /api/auth/install?shop=…) sometimes get
  // shared against this SPA host, where they'd render the app shell
  // instead of starting OAuth. Forward them to the API host verbatim.
  if (window.location.pathname.startsWith('/api/')) {
    window.location.replace(
      `${apiBase}${window.location.pathname}${window.location.search}`
    );
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('shop');
  let shop = fromUrl;
  if (!shop) {
    try { shop = window.localStorage.getItem('autonoma_shop'); } catch { /* private mode */ }
  }
  if (!shop || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shop)) return;
  if (fromUrl) {
    try { window.localStorage.setItem('autonoma_shop', fromUrl); } catch { /* private mode */ }
  }
  window.location.replace(
    `${apiBase}/api/auth/install?shop=${encodeURIComponent(shop)}`
  );
})();

// Kill any previously registered service worker. An old CRA/PWA worker
// (or a poisoned cache of index.html served at a .js URL) is what left
// the embedded app as a blank page: the browser refused to execute
// /static/js/main.*.js because its cached MIME type was text/html.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((reg) => reg.unregister());
  }).catch(() => {});
  if (typeof window !== 'undefined' && 'caches' in window) {
    caches.keys().then((keys) => {
      keys.forEach((key) => caches.delete(key));
    }).catch(() => {});
  }
}

// Error tracking (optional, graceful): only when REACT_APP_SENTRY_DSN
// is set at build time. Dynamic import keeps the Sentry chunk out of
// the main bundle entirely when unconfigured; a load failure changes
// nothing (the app never depends on monitoring).
if (process.env.REACT_APP_SENTRY_DSN) {
  import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.NODE_ENV || 'production',
      sendDefaultPii: false,
      tracesSampleRate: 0,
      initialScope: { tags: { service: 'frontend' } },
      // Frontend state can embed shop tokens in API error messages —
      // scrub secret-shaped strings before anything leaves the browser.
      beforeSend(event) {
        try {
          const scrub = (s) => String(s)
            .replace(/shp(at|ca|ua|ss)_[a-fA-F0-9]{16,}/g, '[redacted]')
            .replace(/Bearer\s+[A-Za-z0-9._~+/-]{16,}=*/g, '[redacted]')
            .replace(/(key|token|secret|signature|hmac)=[^&\s"']{8,}/gi, '$1=[redacted]');
          for (const ex of event.exception?.values || []) {
            if (ex.value) ex.value = scrub(ex.value);
          }
          if (event.message) event.message = scrub(event.message);
          if (event.request?.url) event.request.url = scrub(event.request.url);
          return event;
        } catch {
          return null; // a scrubbing bug must never leak — drop the event
        }
      },
    });
  }).catch(() => { /* monitoring is optional — app runs without it */ });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
